import {
  put, call, takeLatest, select
} from 'redux-saga/effects';
import { Notification as customNotification } from '../../components/ui/custom-notification';
import { get } from 'lodash';
import {
  CREATE_UPDATE_USER_TABLE_LAYOUT,
  GET_ALL_USER_TABLE_LAYOUTS,
  GET_USER_TABLE_LAYOUT_BY_ID
} from '../types';
import {
  createUpdateUserTableLayoutSuccess,
  createUpdateUserTableLayoutFailure,
  setAllUserTableLayouts,
  setAllUserTableLayoutsFailure,
  setUserTableLayoutById,
  setUserTableLayoutByIdFailure
} from './actions';
import axiosConfig from './axiosConfig';

// Helper function to get userId from Redux state
function* getUserIdFromState() {
  try {
    // Method 1: Try to get from userPermissions (most reliable - set after login)
    const userPermissions = yield select(state => state.userManagement?.userPermissions?.data);
    
    if (userPermissions?.data?.user_id) {
      return userPermissions.data.user_id;
    }
    
    // Method 2: Get cognitoId from auth state and find user in users list
    const authState = yield select(state => state.auth);
    
    if (authState?.user?.sub) {
      const cognitoId = authState.user.sub;
      
      // Try to find user in users list by cognitoUserId
      const users = yield select(state => state.userManagement?.users || []);
      
      const currentUser = users.find(user => user.cognitoUserId === cognitoId);
      if (currentUser?.id) {
        return currentUser.id;
      }
    }
    
    // Method 3: Try to parse from JWT token
    try {
      const token = localStorage.getItem('ap_idToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        
        // Check if user_id is directly in token
        if (payload.user_id) {
          return payload.user_id;
        }
        
        // If we have sub (cognitoId) in token, try to find user
        if (payload.sub) {
          const users = yield select(state => state.userManagement?.users || []);
          const currentUser = users.find(user => user.cognitoUserId === payload.sub);
          if (currentUser?.id) {
            return currentUser.id;
          }
        }
      }
    } catch (e) {
      console.error('[UserTableLayout Saga] Error parsing JWT token:', e);
    }
    
    console.warn('[UserTableLayout Saga] ✗ Could not find userId from any source');
    const hasUsersList = yield select(state => !!(state.userManagement?.users?.length));
    console.warn('[UserTableLayout Saga] Available state:', {
      hasUserPermissions: !!userPermissions,
      hasAuthUser: !!authState?.user,
      hasUsersList: hasUsersList
    });
    
    return null;
  } catch (error) {
    console.error('[UserTableLayout Saga] Error getting userId:', error);
    return null;
  }
}

// API functions
const api = {
  createUpdateUserTableLayout: async (payload) => {
    try {
      const res = await axiosConfig.post('/userTableLayout/createUpdateUserTableLayout', payload);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        const responseData = get(res, ['data'], {});
        // Handle both success: 1 and status: "success" formats
        if (responseData.success === 1 || responseData.status === 'success') {
          return responseData.data;
        } else {
          customNotification.error('Operation failed', responseData.message || 'Failed to create/update user table layout');
          return null;
        }
      }
      return null;
    } catch (error) {
      const errorMessage = get(error, ['response', 'data', 'error'], error.message || 'Failed to create/update user table layout');
      customNotification.error('Operation failed', errorMessage);
      return null;
    }
  },

  getAllUserTableLayouts: async (filters) => {
    try {
      let url = '/userTableLayout/getAllUserTableLayouts';
      if (filters && Object.keys(filters).length > 0) {
        const queryParams = new URLSearchParams();
        Object.keys(filters).forEach(key => {
          if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
            queryParams.append(key, filters[key]);
          }
        });
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const res = await axiosConfig.get(url);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        const responseData = get(res, ['data'], {});
        // Handle both success: 1 and status: "success" formats
        if (responseData.success === 1 || responseData.status === 'success') {
          const layouts = get(responseData, ['data', 'layouts'], []);
          const total = get(responseData, ['data', 'total'], 0);
          return { layouts, total };
        } else {
          customNotification.error('Operation failed', responseData.message || 'Failed to fetch user table layouts');
          return null;
        }
      }
      return { layouts: [], total: 0 };
    } catch (error) {
      const errorMessage = get(error, ['response', 'data', 'error'], error.message || 'Failed to fetch user table layouts');
      customNotification.error('Operation failed', errorMessage);
      return null;
    }
  },

  getUserTableLayoutById: async (id) => {
    try {
      const res = await axiosConfig.get(`/userTableLayout/getUserTableLayoutById/${id}`);

      if (res.status === 403) {
        customNotification.error('Unauthorized', 'You do not have permission to access this resource.');
        return null;
      }
      if (res.status === 200) {
        const responseData = get(res, ['data'], {});
        // Handle both success: 1 and status: "success" formats
        if (responseData.success === 1 || responseData.status === 'success') {
          const layouts = get(responseData, ['data', 'layouts'], []);
          return layouts.length > 0 ? layouts[0] : null;
        } else {
          customNotification.error('Operation failed', responseData.message || 'Failed to fetch user table layout');
          return null;
        }
      }
      return null;
    } catch (error) {
      const errorMessage = get(error, ['response', 'data', 'error'], error.message || 'Failed to fetch user table layout');
      customNotification.error('Operation failed', errorMessage);
      return null;
    }
  }
};

// Sagas
function* createUpdateUserTableLayoutSaga(action) {
  try {
    // Get userId from state if not in payload
    let payload = { ...action.payload };
    if (!payload.user_id) {
      const userId = yield getUserIdFromState();
      if (userId) {
        payload.user_id = userId;
      } else {
        console.error('[UserTableLayout Saga] Cannot create/update layout without userId');
        yield put(createUpdateUserTableLayoutFailure('User ID is required'));
        return;
      }
    }
    
    const result = yield call(api.createUpdateUserTableLayout, payload);
    if (result !== null) {
      yield put(createUpdateUserTableLayoutSuccess(result));
      customNotification.success('Layout saved successfully', 'The user table layout has been saved successfully.');
    } else {
      yield put(createUpdateUserTableLayoutFailure('Failed to create/update user table layout'));
    }
  } catch (error) {
    yield put(createUpdateUserTableLayoutFailure(error.message));
  }
}

function* getAllUserTableLayoutsSaga(action) {
  try {
    // Get userId from state if not in filters
    let filters = { ...(action.filters || {}) };
    if (!filters.user_id) {
      const userId = yield getUserIdFromState();
      if (userId) {
        filters.user_id = userId;
      } else {
        console.warn('[UserTableLayout Saga] No userId found, fetching all layouts (may be empty)');
      }
    }
    
    const result = yield call(api.getAllUserTableLayouts, filters);
    if (result !== null) {
      yield put(setAllUserTableLayouts(result.layouts, result.total));
    } else {
      yield put(setAllUserTableLayoutsFailure('Failed to fetch user table layouts'));
    }
  } catch (error) {
    yield put(setAllUserTableLayoutsFailure(error.message));
  }
}

function* getUserTableLayoutByIdSaga(action) {
  try {
    const result = yield call(api.getUserTableLayoutById, action.id);
    if (result !== null) {
      yield put(setUserTableLayoutById(result));
    } else {
      yield put(setUserTableLayoutByIdFailure('User table layout not found'));
    }
  } catch (error) {
    yield put(setUserTableLayoutByIdFailure(error.message));
  }
}

// Root saga
export function* userTableLayoutSaga() {
  yield takeLatest(CREATE_UPDATE_USER_TABLE_LAYOUT, createUpdateUserTableLayoutSaga);
  yield takeLatest(GET_ALL_USER_TABLE_LAYOUTS, getAllUserTableLayoutsSaga);
  yield takeLatest(GET_USER_TABLE_LAYOUT_BY_ID, getUserTableLayoutByIdSaga);
}

